import CircularProgress from '@material-ui/core/CircularProgress';
import CopyWithTooltip from 'components/CopyWithTooltip';
import DialogActions from '@material-ui/core/DialogActions';
import Field from 'components/Field';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import MuiRadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import React, { useEffect, useMemo, useState } from 'react';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { Box, Button, Dialog, Grid } from '@attain-sre/attain-design-system';
import {
  CANCEL,
  CAPI,
  CLIENT_NAME,
  CONNECTION_TYPE,
  COPY_OAUTH_URL,
  CREATE_OAUTH_URL,
  DESCRIPTION,
  PLATFORM,
  VIANT_ACCOUNT_ID,
} from '../../../content';
import { CreateOAuthUrlPayload } from '../types';
import { Formik } from 'formik';
import {
  OAUTH_AD_ACCOUNT_LABELS,
  OAUTH_PLATFORMS,
  OAUTH_PLATFORM_LABELS,
  VIANT_CONNECTION_TYPES,
} from '../constants';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { createOAuthUrl, resetCreateOAuthUrlState } from '../slice';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useUser } from 'reactfire';
import * as Styled from './index.styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3),
      overflowX: 'hidden',
      '& .MuiDialog-paper': {
        overflowX: 'hidden',
      },
    },
    field: {
      marginTop: 0,
      marginBottom: 0,
      height: 48,
      '& .MuiInputBase-root': {
        height: 48,
        borderRadius: 10,
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        height: 48,
      },
      '& .MuiSelect-root': {
        height: 48,
      },
      '& .MuiSelect-select': {
        height: 48,
        display: 'flex',
        alignItems: 'center',
      },
    },
    textareaField: {
      marginTop: 0,
      marginBottom: 0,
      '& .MuiInputBase-root': {
        minHeight: 48,
        borderRadius: 10,
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
      },
    },
    gridRow: {
      marginBottom: theme.spacing(3),
    },
    labelColumn: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: theme.spacing(2),
    },
    controlColumn: {
      flex: 1,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 23,
      paddingLeft: 16,
      paddingTop: 16,
    },
    successContainer: {
      padding: 0,
    },
    oauthUrlContainer: {
      padding: theme.spacing(2),
      backgroundColor: '#f5f5f5',
      borderRadius: 4,
      wordBreak: 'break-all',
    },
    oauthUrlText: {
      fontSize: 14,
      fontFamily: 'monospace',
    },
    radioGroup: {
      flexDirection: 'row',
      gap: theme.spacing(2),
    },
    radioLabel: {
      marginRight: 0,
    },
  })
);

interface CreateOAuthUrlModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateOAuthUrlModal = ({
  open,
  onClose,
  onSuccess,
}: CreateOAuthUrlModalProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { data: user } = useUser();
  const { uiOAuthAmazonVisible = false } = useFlags();

  const platformOptions = useMemo(
    () =>
      [
        {
          value: OAUTH_PLATFORMS.AMAZON,
          label: OAUTH_PLATFORM_LABELS[OAUTH_PLATFORMS.AMAZON],
        },
        {
          value: OAUTH_PLATFORMS.REDDIT,
          label: OAUTH_PLATFORM_LABELS[OAUTH_PLATFORMS.REDDIT],
        },
        {
          value: OAUTH_PLATFORMS.TIKTOK,
          label: OAUTH_PLATFORM_LABELS[OAUTH_PLATFORMS.TIKTOK],
        },
        {
          value: OAUTH_PLATFORMS.VIANT,
          label: OAUTH_PLATFORM_LABELS[OAUTH_PLATFORMS.VIANT],
        },
      ].filter((opt) => {
        if (opt.value === OAUTH_PLATFORMS.AMAZON) {
          return uiOAuthAmazonVisible;
        }
        return true;
      }),
    [uiOAuthAmazonVisible]
  );

  const [platform, setPlatform] = useState<
    'amazon' | 'reddit' | 'tiktok' | 'viant' | ''
  >('');
  const [clientName, setClientName] = useState('');
  const [description, setDescription] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  const [viantAccountId, setViantAccountId] = useState('');

  const createLoading = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.createLoading.state === 'loading'
  );
  const createSuccess = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.createSuccess
  );
  const createdOAuthUrl = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.createdOAuthUrl
  );

  const isViantPlatform = platform === OAUTH_PLATFORMS.VIANT;

  useEffect(() => {
    if (createSuccess && createdOAuthUrl) {
      // Success state - form will be hidden and OAuth URL shown
    }
  }, [createSuccess, createdOAuthUrl]);

  useEffect(() => {
    setAdAccountId('');
    setViantAccountId('');
  }, [platform]);

  useEffect(() => {
    if (!uiOAuthAmazonVisible && platform === OAUTH_PLATFORMS.AMAZON) {
      setPlatform('');
    }
  }, [uiOAuthAmazonVisible, platform]);

  const resetForm = () => {
    setPlatform('');
    setClientName('');
    setDescription('');
    setAdAccountId('');
    setViantAccountId('');
  };

  const handleClose = () => {
    resetForm();
    dispatch(resetCreateOAuthUrlState());
    onClose();
  };

  const handleAdAccountIdChange = (value: string) => {
    if (platform === OAUTH_PLATFORMS.AMAZON) {
      const numericValue = value.replace(/[^0-9]/g, '');
      setAdAccountId(numericValue);
    } else {
      setAdAccountId(value);
    }
  };

  const handleSubmit = () => {
    if (!platform || !clientName || !user?.email) {
      return;
    }

    const payload: CreateOAuthUrlPayload = {
      platform: platform as 'amazon' | 'reddit' | 'tiktok' | 'viant',
      clientName: clientName.trim(),
      description: description.trim() || undefined,
      createdBy: user.email,
    };

    if (platform === OAUTH_PLATFORMS.AMAZON) {
      const trimmedAdAccountId = adAccountId.trim();
      if (!trimmedAdAccountId) return;
      payload.amazonAdvertiserId = Number(trimmedAdAccountId);
    } else if (platform === OAUTH_PLATFORMS.REDDIT) {
      const trimmedAdAccountId = adAccountId.trim();
      if (!trimmedAdAccountId) return;
      payload.redditAdAccountId = trimmedAdAccountId;
    } else if (platform === OAUTH_PLATFORMS.TIKTOK) {
      const trimmedAdAccountId = adAccountId.trim();
      if (!trimmedAdAccountId) return;
      payload.tiktokAdvertiserId = trimmedAdAccountId;
    } else if (platform === OAUTH_PLATFORMS.VIANT) {
      const trimmedViantAccountId = viantAccountId.trim();
      if (!trimmedViantAccountId) return;
      payload.viantAccountId = trimmedViantAccountId;
      payload.connectionType = VIANT_CONNECTION_TYPES.CAPI;
    }

    dispatch(createOAuthUrl(payload));
  };

  const getAdAccountLabel = () => {
    if (!platform) return 'Ad Account ID';
    return (
      OAUTH_AD_ACCOUNT_LABELS[
        platform as keyof typeof OAUTH_AD_ACCOUNT_LABELS
      ] || 'Ad Account ID'
    );
  };

  const isFormValid = useMemo(() => {
    if (platform === '' || clientName.trim() === '' || !user?.email) {
      return false;
    }

    if (isViantPlatform) {
      return viantAccountId.trim() !== '';
    }

    return adAccountId.trim() !== '';
  }, [
    platform,
    clientName,
    user?.email,
    isViantPlatform,
    viantAccountId,
    adAccountId,
  ]);

  const showSuccess = createSuccess && createdOAuthUrl;

  const renderTextFieldRow = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    id: string,
    required = false,
    inputProps?: React.ComponentProps<typeof Field>
  ) => (
    <Grid container className={classes.gridRow}>
      <Grid item xs={4} className={classes.labelColumn}>
        <Styled.BodyHeader>
          {label} {required && <span>*</span>}
        </Styled.BodyHeader>
      </Grid>
      <Grid item xs={8} className={classes.controlColumn}>
        <Formik>
          <Field
            className={classes.field}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(e.target.value)
            }
            value={value}
            fullWidth
            id={id}
            type="text"
            {...inputProps}
          />
        </Formik>
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      className={classes.root}
      title={CREATE_OAUTH_URL}
      onClose={handleClose}
      open={open}
      headerID="createOAuthUrlHeader"
      header={
        <Styled.CardHeader>
          <Styled.CardTitle>{CREATE_OAUTH_URL}</Styled.CardTitle>
        </Styled.CardHeader>
      }
      bodyID="createOAuthUrlBody"
      body={
        showSuccess ? (
          <div className={classes.successContainer}>
            <div className={classes.oauthUrlContainer}>
              <div className={classes.oauthUrlText}>
                {createdOAuthUrl?.oauthUrl}
              </div>
            </div>
          </div>
        ) : (
          <Styled.StyledBodyContainer container spacing={0}>
            <Grid container className={classes.gridRow}>
              <Grid item xs={4} className={classes.labelColumn}>
                <Styled.BodyHeader>
                  {PLATFORM} <span>*</span>
                </Styled.BodyHeader>
              </Grid>
              <Grid item xs={8} className={classes.controlColumn}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.field}
                >
                  <Select
                    value={platform}
                    onChange={(e: any) => setPlatform(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Platform
                    </MenuItem>
                    {platformOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {isViantPlatform && (
              <Grid container className={classes.gridRow}>
                <Grid item xs={4} className={classes.labelColumn}>
                  <Styled.BodyHeader>
                    {CONNECTION_TYPE} <span>*</span>
                  </Styled.BodyHeader>
                </Grid>
                <Grid item xs={8} className={classes.controlColumn}>
                  <MuiRadioGroup
                    className={classes.radioGroup}
                    value={VIANT_CONNECTION_TYPES.CAPI}
                  >
                    <FormControlLabel
                      className={classes.radioLabel}
                      value={VIANT_CONNECTION_TYPES.CAPI}
                      control={<Radio color="primary" />}
                      label={CAPI}
                    />
                  </MuiRadioGroup>
                </Grid>
              </Grid>
            )}

            {renderTextFieldRow(
              CLIENT_NAME,
              clientName,
              setClientName,
              'clientName',
              true
            )}

            <Grid container className={classes.gridRow}>
              <Grid item xs={4} className={classes.labelColumn}>
                <Styled.BodyHeader>{DESCRIPTION}</Styled.BodyHeader>
              </Grid>
              <Grid item xs={8} className={classes.controlColumn}>
                <TextField
                  className={classes.textareaField}
                  onChange={(
                    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                  ) => setDescription(e.target.value)}
                  value={description}
                  fullWidth
                  id="description"
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter description (optional)"
                />
              </Grid>
            </Grid>

            {isViantPlatform
              ? renderTextFieldRow(
                  VIANT_ACCOUNT_ID,
                  viantAccountId,
                  setViantAccountId,
                  'viantAccountId',
                  true
                )
              : renderTextFieldRow(
                  getAdAccountLabel(),
                  adAccountId,
                  handleAdAccountIdChange,
                  'adAccountId',
                  true
                )}
          </Styled.StyledBodyContainer>
        )
      }
      footer={
        <DialogActions>
          {showSuccess ? (
            <>
              {createdOAuthUrl?.oauthUrl && (
                <CopyWithTooltip value={createdOAuthUrl.oauthUrl}>
                  <Button color="primary" variant="contained">
                    {COPY_OAUTH_URL}
                  </Button>
                </CopyWithTooltip>
              )}
              <Button color="primary" onClick={handleClose} variant="outlined">
                {CANCEL}
              </Button>
            </>
          ) : (
            <>
              <Button color="primary" onClick={handleClose} variant="outlined">
                {CANCEL}
              </Button>
              {createLoading ? (
                <Box
                  width={142}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CircularProgress color="primary" size={20} />
                </Box>
              ) : (
                <Button
                  color="primary"
                  disabled={!isFormValid}
                  disableElevation
                  onClick={handleSubmit}
                  variant="contained"
                >
                  Create
                </Button>
              )}
            </>
          )}
        </DialogActions>
      }
    />
  );
};

export default CreateOAuthUrlModal;
